import { curatedLeagueName } from "@softmax/web-features/coworlds";
import { buildFeed } from "../lib/activity/build-feed";
import type { PlayActivityData } from "../lib/types";
import type { PlayApi } from "./play-api";
import type { PlayShellInternal } from "./play-shell";
export async function readPlayActivity({
  api,
  shell,
}: {
  readonly api: PlayApi;
  readonly shell: PlayShellInternal;
}): Promise<PlayActivityData> {
  const generatedAt = new Date().toISOString();
  const policyName = shell.seat?.policy_name ?? null;

  if (!shell.signedIn)
    return { league: shell.league, generatedAt, policyName, feed: [] };

  const [events, submissions, versions] = await Promise.all([
    shell.seat
      ? api.competitionEvents({
          playerId: shell.seat.player_id,
          limit: 200,
        })
      : [],
    api.mySubmissions({}),
    api.myPolicyVersions(),
  ]);

  if (events === null || submissions === null || versions === null)
    throw new Error("Activity is unavailable");

  const seatsByLeague = new Map(
    shell.seats.map((seat) => [seat.league_id, seat]),
  );
  const feed = buildFeed({
    events: events.map((event) => {
      const payload = event.payload;
      const seat =
        "league_id" in payload ? seatsByLeague.get(payload.league_id) : null;
      return {
        event_type: event.event_type,
        headline: event.headline,
        created_at: event.created_at,
        payload: {
          league_name:
            "league_name" in payload
              ? curatedLeagueName({ name: payload.league_name })
              : null,
          division_name:
            "division_name" in payload ? payload.division_name : null,
          policy_name:
            "policy_name" in payload && typeof payload.policy_name === "string"
              ? payload.policy_name
              : null,
        },
        policy_version: event.policy_version ?? null,
        isChampion:
          seat?.is_champion === true &&
          "policy_version_id" in payload &&
          seat.policy_version_id === payload.policy_version_id,
      };
    }),
    submissions: submissions.map((submission) => ({
      created_at: submission.created_at,
      status: submission.status,
      league: { name: curatedLeagueName(submission.league) },
      policy_version: submission.policy_version,
    })),
    policyVersions: versions.rows.map((version) => ({
      policy_name: version.policy_name,
      version: version.version,
      created_at: version.created_at,
    })),
    policyName,
    now: generatedAt,
  });
  return { league: shell.league, generatedAt, policyName, feed };
}
